import cookie from '../utils/cookies';

/**
 * The root element that contains all potentially targeted elements for this
 * module.
 *
 * @type {HTMLElement}
 */
const $root         = document.querySelector( '.property-index' );
const siteContainer = document.querySelector( '.site-container' );

/**
 * The cookie name for the saved property data.
 *
 * @type {string}
 */
const WISHLIST_KEY = 'wordpress_pickard-saved-properties';

/**
 * The amount of days until the cookie will expire.
 *
 * @type {number}
 */
const WISHLIST_EXPIRATION_DAYS = 31;

/**
 * A list of elements that are needed for this module.
 *
 * @type {{
 *     sortFieldHeader: HTMLElement,
 *     sortFieldFilters: HTMLElement,
 *     filtersForm: HTMLElement,
 *     properties: NodeListOf<HTMLElement>, 
 *     propertyCount: HTMLElement,
 *     propertyTitlesText: HTMLElement,
 * }}
 */
const $elements = {};

/**
 * Sets up the $elements object.
 *
 * Called once the $root element has been confirmed to exist.
 *
 * @return void
 */
function setupElements() {
	$elements.sortFieldHeader    = $root.querySelector( '#property-sort-search-header' );
	$elements.sortFieldFilters   = $root.querySelector( '#property-sort-primary' );
	$elements.filtersForm        = $root.querySelector( 'form.property-sidebar__wrap' );
	$elements.properties         = $root.querySelectorAll( '.property-card' );
	$elements.propertyCount      = $root.querySelector( '.property-index__titles--count' );
	$elements.propertyTitlesText = $root.querySelector( '.property-index__titles--text' );
} 

/**
 * Modifies the output of the sort dropdown in the filters to match the element
 * in the header, once it's been updated, and then submits the form.
 *
 * @param event
 *
 * @return void
 */
function matchSortFields( event ) {
	if ( event.target !== $elements.sortFieldHeader ) {
		return;
	}

	$elements.sortFieldFilters.value = event.target.value;
	$elements.filtersForm.submit();
	if ( siteContainer ) {
		siteContainer.classList.add('site-container--loading');
	}

}

/**
 * Update the user's cookie to potentially save or un-save a property in their
 * wishlist.
 *
 * @param {HTMLElement} $property
 *
 * @return void
 */
function togglePropertySaveState( $property ) {

	if ( ! $property.dataset.id ) {
		return;
	}

	const id = parseInt( $property.dataset.id );

	let wishlist = cookie.get( WISHLIST_KEY );

	if ( wishlist === undefined ) {
		wishlist = [];
	} else {
		wishlist = wishlist.split( ',' )
			.map( ( value ) => parseInt( value ) )
			.filter( ( value ) => value > 0 );
	}	

	let values = ! wishlist.includes( id ) ?
		[ ...wishlist, id ] :
		wishlist.filter( ( value ) => value !== id );

	cookie.set( WISHLIST_KEY, values, { expires: WISHLIST_EXPIRATION_DAYS, path: '/' } )

	if ( $root.classList.contains( 'property-index--wishlist' ) ) {
		$elements.propertyCount.innerHTML = values.length;
		if ( 1 === values.length ) {
			$elements.propertyTitlesText.innerHTML = 'property';
		} else {
			$elements.propertyTitlesText.innerHTML = 'properties';
		}

		if ( $property.classList.contains( 'property-card--saved' ) ) {
			$property.remove();
		}
	}

	$property.classList.toggle( 'property-card--saved' );
}

/**
 * Potentially handle multiple events when a user clicks on a property.
 *
 * @param {Event} event
 *
 * @return bool
 */
function handlePropertyClicks( event ) {
	const $target = event.target;
	let $property = $target;

	if ( ! $target.classList.contains( 'property-card' ) ) {
		$property = $target.closest( '.property-card' );		
	}

	if ( $target.classList.contains( 'property-card__bookmark' ) ) {		
		event.preventDefault();
		event.stopPropagation();

		togglePropertySaveState( $property );
	}
}

/**
 * Constructor.
 *
 * @return void
 */
export default function propertySearch() {

	if ( ! $root ) {
		return;
	}

	setupElements();

	if ( $elements.sortFieldHeader && $elements.sortFieldFilters && $elements.filtersForm ) {
		$elements.sortFieldHeader.addEventListener( 'change', matchSortFields );
	}

	if ( $elements.properties && $elements.propertyCount && $elements.propertyTitlesText ) {
		$elements.properties.forEach(
			( $property ) => $property.addEventListener( 'click', handlePropertyClicks )
		);
	}
}
